import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Phone, MapPin, User as UIcon, ArrowRight, ShieldCheck, KeyRound } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { properCase, validateUKPhone, validateUKPostcode, validateEmail } from '../utils/validators';

const emptyForm = {
  title: 'Mr', firstName: '', lastName: '', email: '', mobile: '',
  address1: '', address2: '', city: '', county: '', postcode: '',
  password: '',
};

const Register = () => {
  const nav = useNavigate();
  const { toast } = useToast();
  const { startEmailOtp, pendingOtp, verifyOtp, register, clearPendingOtp } = useAuth();
  const [f, setF] = useState(emptyForm);
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('form'); // form | otp
  const [busy, setBusy] = useState(false);

  const upd = (patch) => setF((s) => ({ ...s, ...patch }));

  const startVerification = async (e) => {
    e.preventDefault();
    const fn = properCase(f.firstName.trim());
    const ln = properCase(f.lastName.trim());
    if (!fn || !ln) return toast({ title: 'Please enter your first and last name' });
    const em = validateEmail(f.email);
    if (!em.ok) return toast({ title: em.error });
    const ph = validateUKPhone(f.mobile);
    if (!ph.ok) return toast({ title: ph.error });
    const pc = validateUKPostcode(f.postcode);
    if (!pc.ok) return toast({ title: pc.error });
    if (!f.address1.trim() || !f.city.trim() || !f.county.trim()) return toast({ title: 'Please complete address line 1, town and county' });
    if ((f.password || '').length < 6) return toast({ title: 'Choose a password with at least 6 characters' });

    const payload = { ...f, firstName: fn, lastName: ln, name: `${fn} ${ln}`, email: em.formatted, mobile: ph.formatted, postcode: pc.formatted };
    setBusy(true);
    const res = await startEmailOtp(em.formatted, 'register', payload);
    setBusy(false);
    if (!res.ok) return toast({ title: res.error });
    setStep('otp');
    toast({ title: 'Verification code sent', description: `Check ${em.formatted} for a 6-digit code.` });
  };

  const confirmOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    const v = await verifyOtp(otp.trim());
    setBusy(false);
    if (!v.ok) return toast({ title: v.error });
    const r = register(v.payload, v.token);
    if (!r.ok) return toast({ title: r.error });
    toast({ title: 'Welcome to Domestic Home Services!' });
    nav('/dashboard');
  };

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-5">
          <div className="bg-white rounded-3xl p-6 md:p-10 border border-[#1F0068]/5 shadow-xl">
            {step === 'form' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#FCE0EC] flex items-center justify-center text-[#1F0068]"><UIcon className="w-5 h-5" /></div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-[#1F0068]">Create your account</h1>
                    <p className="text-sm text-[#1F0068]/60">Manage plans, claims and repairs in one place.</p>
                  </div>
                </div>
                <form onSubmit={startVerification} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Title</Label>
                    <Select value={f.title} onValueChange={(v) => upd({ title: v })}>
                      <SelectTrigger className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"><SelectValue /></SelectTrigger>
                      <SelectContent>{['Mr', 'Mrs', 'Ms', 'Miss', 'Mx', 'Dr', 'Prof'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-1" />
                  <div>
                    <Label className="text-[#1F0068] font-semibold">First Name *</Label>
                    <Input value={f.firstName} onChange={(e) => upd({ firstName: e.target.value })} onBlur={(e) => upd({ firstName: properCase(e.target.value) })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Last Name *</Label>
                    <Input value={f.lastName} onChange={(e) => upd({ lastName: e.target.value })} onBlur={(e) => upd({ lastName: properCase(e.target.value) })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Email *</Label>
                    <div className="relative mt-2"><Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input type="email" value={f.email} onChange={(e) => upd({ email: e.target.value })} placeholder="you@example.com" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                    <div className="text-[10px] text-[#1F0068]/50 mt-1">Disposable/temporary emails are not accepted.</div>
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Mobile Number *</Label>
                    <div className="relative mt-2"><Phone className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input value={f.mobile} onChange={(e) => upd({ mobile: e.target.value })} placeholder="07123 456789" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#1F0068] font-semibold">Address Line 1 *</Label>
                    <div className="relative mt-2"><MapPin className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input value={f.address1} onChange={(e) => upd({ address1: e.target.value })} placeholder="House number and street" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[#1F0068] font-semibold">Address Line 2</Label>
                    <Input value={f.address2} onChange={(e) => upd({ address2: e.target.value })} placeholder="Optional" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Town / City *</Label>
                    <Input value={f.city} onChange={(e) => upd({ city: e.target.value })} onBlur={(e) => upd({ city: properCase(e.target.value) })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">County *</Label>
                    <Input value={f.county} onChange={(e) => upd({ county: e.target.value })} onBlur={(e) => upd({ county: properCase(e.target.value) })} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Postcode *</Label>
                    <Input value={f.postcode} onChange={(e) => upd({ postcode: e.target.value.toUpperCase() })} placeholder="SW1A 1AA" className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
                  </div>
                  <div>
                    <Label className="text-[#1F0068] font-semibold">Password *</Label>
                    <div className="relative mt-2"><Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" /><Input type="password" value={f.password} onChange={(e) => upd({ password: e.target.value })} placeholder="At least 6 characters" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" /></div>
                  </div>
                  <div className="md:col-span-2 pt-2">
                    <Button type="submit" className="w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                      Continue to email verification <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <div className="text-center text-sm text-[#1F0068]/70 pt-3">
                      Already have an account? <Link to="/login" className="font-bold text-[#FF5C39] hover:underline">Log in</Link>
                    </div>
                  </div>
                </form>
              </>
            )}

            {step === 'otp' && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-[#FFEDD6] flex items-center justify-center text-[#1F0068]"><KeyRound className="w-5 h-5" /></div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-[#1F0068]">Verify your email</h1>
                    <p className="text-sm text-[#1F0068]/60">Enter the 6-digit code we sent to <b>{f.email}</b>.</p>
                  </div>
                </div>
                <div className="bg-[#FFEDD6] border border-[#FF5C39]/30 rounded-xl p-4 mb-5 text-sm text-[#1F0068]">
                  We’ve emailed a 6-digit code to <b>{f.email}</b>. Please check your inbox (and spam folder).
                </div>
                <form onSubmit={confirmOtp}>
                  <Label className="text-[#1F0068] font-semibold">Verification code</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))} maxLength={6} placeholder="6-digit code" className="h-12 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10 text-center text-2xl tracking-widest" />
                  <Button type="submit" disabled={busy} className="mt-4 w-full h-12 rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white font-semibold">
                    {busy ? 'Verifying…' : (<>Verify & create account <ShieldCheck className="w-4 h-4 ml-1" /></>)}
                  </Button>
                  <button type="button" onClick={() => { clearPendingOtp(); setStep('form'); }} className="mt-3 text-xs text-[#1F0068]/70 underline">Back to form</button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Register;
